import { useState, useEffect, useCallback } from 'react'
import { authFetch } from '../../lib/api'
import { usePulseMemory } from '../PulseMemory/usePulseMemory'

interface Artifact {
  id: string
  name: string
  type: string
  version: number
  created_at: string
  updated_at: string
  content?: string
}

const TYPE_ICONS: Record<string, string> = { code: '💻', doc: '📄', diagram: '📊', config: '⚙️', other: '📎' }

export function ArtifactsPanel() {
  const project = usePulseMemory(s => s.memory.project) || 'default'
  const [artifacts, setArtifacts] = useState<Artifact[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [viewing, setViewing] = useState<Artifact | null>(null)
  const [form, setForm] = useState({ name: '', type: 'code', content: '' })

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const r = await authFetch(`/api/artifacts/${encodeURIComponent(project)}`)
      if (r.ok) setArtifacts(await r.json())
    } catch {}
    setLoading(false)
  }, [project])

  useEffect(() => { load() }, [load])

  const handleCreate = async () => {
    if (!form.name || !form.content) return
    await authFetch('/api/artifacts', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ project, ...form }),
    })
    setForm({ name: '', type: 'code', content: '' })
    setCreating(false)
    load()
  }

  const handleView = async (id: string) => {
    const r = await authFetch(`/api/artifacts/view/${id}`)
    if (r.ok) setViewing(await r.json())
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Delete this artifact?')) return
    await authFetch(`/api/artifacts/${id}`, { method: 'DELETE' })
    load()
  }

  const s: React.CSSProperties = { padding: 20, height: '100%', overflow: 'auto', fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--text-1)' }
  const card: React.CSSProperties = { background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 8 }
  const btn: React.CSSProperties = { padding: '6px 12px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', cursor: 'pointer', fontSize: 11, fontFamily: 'var(--font-mono)' }
  const input: React.CSSProperties = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg-3)', color: 'var(--text-1)', fontSize: 11, fontFamily: 'var(--font-mono)', boxSizing: 'border-box' }

  // Viewing an artifact
  if (viewing) {
    return (
      <div style={s}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
          <button style={btn} onClick={() => setViewing(null)}>← Back</button>
          <span style={{ fontWeight: 600 }}>{TYPE_ICONS[viewing.type] || '📎'} {viewing.name}</span>
          <span style={{ fontSize: 10, color: 'var(--text-2)', marginLeft: 'auto' }}>v{viewing.version}</span>
        </div>
        <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
          <button style={btn} onClick={() => navigator.clipboard.writeText(viewing.content || '')}>📋 Copy</button>
        </div>
        <pre style={{ margin: 0, padding: 12, background: 'var(--bg-1)', border: '1px solid var(--border)', borderRadius: 6, overflow: 'auto', maxHeight: 500, fontSize: 11, whiteSpace: 'pre-wrap', color: 'var(--text-2)' }}>{viewing.content}</pre>
      </div>
    )
  }

  return (
    <div style={s}>
      <h3 style={{ margin: '0 0 4px', fontSize: 14, color: 'var(--accent)' }}>📦 Artifacts</h3>
      <p style={{ margin: '0 0 16px', color: 'var(--text-2)', fontSize: 11 }}>
        Saved code, docs, diagrams for: <strong>{project}</strong>
      </p>

      {!creating ? (
        <button style={{ ...btn, marginBottom: 16, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={() => setCreating(true)}>
          + Save Artifact
        </button>
      ) : (
        <div style={{ ...card, marginBottom: 16 }}>
          <input style={{ ...input, marginBottom: 8 }} placeholder="Artifact name..." value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
          <select style={{ ...input, marginBottom: 8, appearance: 'auto' as any }} value={form.type} onChange={e => setForm({ ...form, type: e.target.value })}>
            <option value="code">💻 Code</option>
            <option value="doc">📄 Document</option>
            <option value="diagram">📊 Diagram</option>
            <option value="config">⚙️ Config</option>
            <option value="other">📎 Other</option>
          </select>
          <textarea style={{ ...input, height: 120, resize: 'vertical', marginBottom: 8 }} placeholder="Paste content..." value={form.content} onChange={e => setForm({ ...form, content: e.target.value })} />
          <div style={{ display: 'flex', gap: 8 }}>
            <button style={{ ...btn, background: 'var(--accent)', color: '#fff', border: 'none' }} onClick={handleCreate}>Save</button>
            <button style={btn} onClick={() => setCreating(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div style={{ color: 'var(--text-2)' }}>Loading...</div> : (
        artifacts.length === 0 ? <div style={{ color: 'var(--text-2)' }}>No artifacts yet.</div> : (
          artifacts.map(a => (
            <div key={a.id} style={{ ...card, display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }} onClick={() => handleView(a.id)}>
              <span style={{ fontSize: 18 }}>{TYPE_ICONS[a.type] || '📎'}</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 600, fontSize: 11 }}>{a.name}</div>
                <div style={{ fontSize: 10, color: 'var(--text-2)' }}>v{a.version} · {new Date(a.updated_at).toLocaleDateString()}</div>
              </div>
              <button onClick={e => { e.stopPropagation(); handleDelete(a.id) }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: 4 }}>🗑️</button>
            </div>
          ))
        )
      )}
    </div>
  )
}
